import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Divider } from '@betrybe/ada-react';
import {
  AdjustmentDate,
  BoldText,
  CloseButton,
  Container,
  ModalContent,
  ModalContentWrapper,
  ModalDescription,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Row,
  Text,
  Title,
} from './styles';
import { useIpcaAdjustment } from '@/services/balance';
import type { IpcaAdjustmentType } from '@/services/balance/useIpcaAdjustment';
import { formatToBRL } from '@/utils';

type IpcaAdjustmentModal = {
  contractId: string;
  closeModal: () => void;
  initialBalanceValue: string;
  modalOpened: boolean;
};

function IpcaAdjustmentModal(props: IpcaAdjustmentModal): JSX.Element | null {
  const { contractId, closeModal, modalOpened, initialBalanceValue } = props;
  const { getIpcaAdjustments } = useIpcaAdjustment();
  const [unorderedIpcaAdjustments, setIpcaAdjustments] = useState<
    IpcaAdjustmentType[]
  >([]);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const ipcaAdjustments = useMemo(
    () =>
      [...unorderedIpcaAdjustments].sort((a, b) => {
        const dateA = new Date(a.date_ipca);
        const dateB = new Date(b.date_ipca);

        return dateB.getTime() - dateA.getTime();
      }),
    [unorderedIpcaAdjustments],
  );

  const handleAdjustmentDate = (date: string): string => {
    const dateParts = date.split('-');
    const day = dateParts[2];
    const month = dateParts[1];
    const year = dateParts[0];

    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    if (!modalOpened || !contractId) {
      return undefined;
    }

    let isMounted = true;

    getIpcaAdjustments(contractId).then((data) => {
      if (isMounted) {
        setIpcaAdjustments(data ?? []);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [contractId, getIpcaAdjustments, modalOpened]);

  useEffect(() => {
    if (!modalOpened) {
      return undefined;
    }

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusableSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const currentModal = modalRef.current;
    const firstFocusableElement = currentModal
      ?.querySelectorAll<HTMLElement>(focusableSelector)
      .item(0);

    if (firstFocusableElement) {
      firstFocusableElement.focus();
    } else {
      closeButtonRef.current?.focus();
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();

        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const modalContainer = modalRef.current;

      if (!modalContainer) {
        return;
      }

      const elements = Array.from(
        modalContainer.querySelectorAll<HTMLElement>(focusableSelector),
      ).filter((element) => !element.hasAttribute('disabled'));

      if (elements.length === 0) {
        event.preventDefault();
        closeButtonRef.current?.focus();

        return;
      }

      const firstElement = elements[0];
      const lastElement = elements[elements.length - 1];
      if (!firstElement || !lastElement) {
        event.preventDefault();
        closeButtonRef.current?.focus();

        return;
      }

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    const originalBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [closeModal, modalOpened]);

  if (!modalOpened) {
    return null;
  }

  return createPortal(
    <ModalOverlay onClick={closeModal}>
      <ModalContent
        aria-describedby="ipca-adjustment-modal-description"
        aria-labelledby="ipca-adjustment-modal-title"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
        ref={modalRef}
        role="dialog"
      >
        <CloseButton
          aria-label="Fechar histórico de reajustes"
          onClick={closeModal}
          ref={closeButtonRef}
          type="button"
        >
          ×
        </CloseButton>

        <ModalHeader>
          <ModalTitle id="ipca-adjustment-modal-title">
            Histórico de reajustes
          </ModalTitle>
          <ModalDescription id="ipca-adjustment-modal-description">
            O saldo em aberto é reajustado anualmente pelo IPCA.
          </ModalDescription>
        </ModalHeader>

        <ModalContentWrapper>
          {ipcaAdjustments.length > 0 &&
            ipcaAdjustments.map((ipcaAdjustment) => (
              <div key={ipcaAdjustment.id || ipcaAdjustment.ipca_adjustment_id}>
                <Title>
                  Reajuste {ipcaAdjustment.date_ipca.substring(0, 4)}
                </Title>
                <AdjustmentDate>
                  {handleAdjustmentDate(ipcaAdjustment.date_ipca)}
                </AdjustmentDate>
                <Container>
                  <Row>
                    <Text>Saldo em aberto</Text>
                    <Text>
                      {formatToBRL(
                        ipcaAdjustment.open_balance_before_adjustment,
                      )}
                    </Text>
                  </Row>
                  <Row>
                    <Text>Ajuste IPCA {ipcaAdjustment.value_tax}%</Text>
                    <Text>+{formatToBRL(ipcaAdjustment.value)}</Text>
                  </Row>
                  <Row>
                    <Text>Saldo reajustado</Text>
                    <BoldText>
                      {formatToBRL(ipcaAdjustment.value_after_adjustment)}
                    </BoldText>
                  </Row>
                </Container>
                <Divider position="horizontal" />
              </div>
            ))}
          <Row>
            <Text>Saldo inicial</Text>
            <BoldText>{formatToBRL(initialBalanceValue)}</BoldText>
          </Row>
          <Divider position="horizontal" />
        </ModalContentWrapper>
      </ModalContent>
    </ModalOverlay>,
    document.body,
  );
}

export default IpcaAdjustmentModal;
